import { useState, useEffect } from 'react';
import { createLogger } from '@/utils/logger/devLogger';

const settingsLogger = createLogger('useSettings');

// MVP Settings type
export interface MVPSettings {
  appName: string;
  appDescription: string;
  dateFormat: string;
  timeFormat: string;
}

// Default settings
const DEFAULT_SETTINGS: MVPSettings = {
  appName: "CheerVue",
  appDescription: "Gym Admin Dashboard",
  dateFormat: "MM/DD/YYYY",
  timeFormat: "12h",
};

export function useSettings(organizationId?: string) {
  const [settings, setSettings] = useState<MVPSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch settings from API
  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = organizationId ? `?organization_id=${organizationId}` : '';
      const response = await fetch(`/api/settings${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch settings');
      }

      if (data.success && data.settings) {
        setSettings(data.settings);
        settingsLogger.info('Settings loaded successfully');
      }
    } catch (err: any) {
      settingsLogger.error('Error fetching settings:', err);
      setError(err.message);
      // Keep default settings on error
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  // Save settings to API
  const saveSettings = async (newSettings: MVPSettings): Promise<boolean> => {
    try {
      setError(null);

      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settings: newSettings,
          organization_id: organizationId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save settings');
      }

      if (data.success) {
        setSettings(newSettings);
        settingsLogger.info('Settings saved successfully');
        return true;
      }

      return false;
    } catch (err: any) {
      settingsLogger.error('Error saving settings:', err);
      setError(err.message);
      return false;
    }
  };

  // Load settings on mount and when organization changes
  useEffect(() => {
    fetchSettings();
  }, [organizationId]);

  return {
    settings,
    setSettings,
    loading,
    error,
    saveSettings,
    refetchSettings: fetchSettings
  };
}

